import type { PageMeta } from '@mm/shared';
import clsx from 'clsx';
import {
  ArrowDown,
  ArrowUp,
  ChevronLeft,
  ChevronRight,
  ChevronsUpDown,
  Columns3,
} from 'lucide-react';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { EmptyState, Spinner } from '../components/ui/display';

export interface Column<T> {
  key: string;
  header: ReactNode;
  render?: (row: T) => ReactNode;
  /** Server-side sort key; omit for unsortable columns. */
  sortKey?: string;
  align?: 'left' | 'right' | 'center';
  className?: string;
  /** Name in the column chooser when the header is not plain text. */
  label?: string;
}

const storageKey = (tableId: string) => `mm.columns.${tableId}`;

function readHidden(tableId: string | undefined): string[] {
  if (!tableId) return [];
  try {
    const raw = window.localStorage.getItem(storageKey(tableId));
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((k): k is string => typeof k === 'string') : [];
  } catch {
    return [];
  }
}

/** Columns the user hid on this table, remembered in this browser. */
function useHiddenColumns(tableId: string | undefined) {
  const [hidden, setHidden] = useState<string[]>(() => readHidden(tableId));
  useEffect(() => setHidden(readHidden(tableId)), [tableId]);
  const update = (next: string[]) => {
    setHidden(next);
    if (!tableId) return;
    try {
      window.localStorage.setItem(storageKey(tableId), JSON.stringify(next));
    } catch {
      // Storage unavailable (private mode): the choice lasts for this page only.
    }
  };
  return [hidden, update] as const;
}

const columnName = <T,>(c: Column<T>) =>
  c.label ?? (typeof c.header === 'string' && c.header ? c.header : null);

/** "Columns" button with a checklist; the first column always stays. */
function ColumnChooser<T>({
  columns,
  hidden,
  onChange,
}: {
  columns: Column<T>[];
  hidden: string[];
  onChange: (hidden: string[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', esc);
    };
  }, [open]);
  const choosable = columns.slice(1).filter((c) => columnName(c));
  if (!choosable.length) return null;
  return (
    <div ref={ref} className="relative flex justify-end px-2 pt-2">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium text-slate-500 hover:bg-slate-100 hover:text-slate-700"
      >
        <Columns3 className="size-3.5" />
        Columns{hidden.length ? ` (${hidden.length} hidden)` : ''}
      </button>
      {open && (
        <div className="absolute top-full right-2 z-30 mt-1 w-56 rounded-lg border border-slate-200 bg-white p-2 shadow-lg">
          <p className="px-2 pb-1 text-xs text-slate-500">Show columns</p>
          <ul className="max-h-72 overflow-y-auto">
            {choosable.map((c) => (
              <li key={c.key}>
                <label className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm text-slate-700 hover:bg-slate-50">
                  <input
                    type="checkbox"
                    className="size-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
                    checked={!hidden.includes(c.key)}
                    onChange={(e) =>
                      onChange(
                        e.target.checked ? hidden.filter((k) => k !== c.key) : [...hidden, c.key],
                      )
                    }
                  />
                  {columnName(c)}
                </label>
              </li>
            ))}
          </ul>
          {hidden.length > 0 && (
            <button
              type="button"
              onClick={() => onChange([])}
              className="mt-1 w-full rounded px-2 py-1 text-left text-xs text-brand-700 hover:bg-slate-50"
            >
              Show all
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export function DataTable<T>({
  columns: allColumns,
  tableId,
  rows,
  rowKey,
  loading,
  sort,
  onSortChange,
  onRowClick,
  empty,
  meta,
  onPageChange,
}: {
  columns: Column<T>[];
  /** Enables the column chooser; the choice is remembered per table id. */
  tableId?: string;
  rows: T[] | undefined;
  rowKey: (row: T) => string;
  loading?: boolean;
  sort?: string;
  onSortChange?: (sort: string) => void;
  onRowClick?: (row: T) => void;
  empty?: ReactNode;
  meta?: PageMeta;
  onPageChange?: (page: number) => void;
}) {
  const [hidden, setHidden] = useHiddenColumns(tableId);
  const columns = allColumns.filter((c, i) => i === 0 || !hidden.includes(c.key));
  const activeKey = sort?.replace(/^-/, '');
  const desc = sort?.startsWith('-');

  const toggleSort = (key: string) => {
    if (!onSortChange) return;
    onSortChange(activeKey === key && !desc ? `-${key}` : key);
  };

  return (
    <div>
      {tableId && <ColumnChooser columns={allColumns} hidden={hidden} onChange={setHidden} />}
      <div className="relative overflow-x-auto">
        <table className="min-w-full divide-y divide-slate-200 text-sm">
          <thead className="bg-slate-50">
            <tr>
              {columns.map((col) => (
                <th
                  key={col.key}
                  scope="col"
                  aria-sort={
                    col.sortKey && activeKey === col.sortKey
                      ? desc
                        ? 'descending'
                        : 'ascending'
                      : undefined
                  }
                  className={clsx(
                    'px-4 py-3 text-xs font-semibold tracking-wide whitespace-nowrap text-slate-500 uppercase',
                    col.align === 'right'
                      ? 'text-right'
                      : col.align === 'center'
                        ? 'text-center'
                        : 'text-left',
                  )}
                >
                  {col.sortKey && onSortChange ? (
                    <button
                      type="button"
                      onClick={() => toggleSort(col.sortKey!)}
                      className="inline-flex items-center gap-1 uppercase hover:text-slate-700"
                    >
                      {col.header}
                      {activeKey === col.sortKey ? (
                        desc ? (
                          <ArrowDown className="size-3.5" />
                        ) : (
                          <ArrowUp className="size-3.5" />
                        )
                      ) : (
                        <ChevronsUpDown className="size-3.5 opacity-40" />
                      )}
                    </button>
                  ) : (
                    col.header
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 bg-white">
            {!loading &&
              rows?.map((row) => (
                <tr
                  key={rowKey(row)}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  className={clsx(onRowClick && 'cursor-pointer hover:bg-slate-50')}
                >
                  {columns.map((col) => (
                    <td
                      key={col.key}
                      className={clsx(
                        'px-4 py-3 align-middle text-slate-700',
                        col.align === 'right'
                          ? 'num text-right'
                          : col.align === 'center'
                            ? 'text-center'
                            : 'text-left',
                        col.className,
                      )}
                    >
                      {col.render
                        ? col.render(row)
                        : String((row as Record<string, unknown>)[col.key] ?? '—')}
                    </td>
                  ))}
                </tr>
              ))}
          </tbody>
        </table>
      </div>
      {loading && <Spinner />}
      {!loading && rows?.length === 0 && (empty ?? <EmptyState title="No records found" />)}
      {meta && onPageChange && meta.total > meta.pageSize && (
        <Pagination meta={meta} onPageChange={onPageChange} />
      )}
    </div>
  );
}

export function Pagination({
  meta,
  onPageChange,
}: {
  meta: PageMeta;
  onPageChange: (page: number) => void;
}) {
  const pages = Math.max(1, Math.ceil(meta.total / meta.pageSize));
  const from = (meta.page - 1) * meta.pageSize + 1;
  const to = Math.min(meta.total, meta.page * meta.pageSize);
  return (
    <div className="flex items-center justify-between gap-4 border-t border-slate-100 px-4 py-3 text-sm text-slate-600">
      <p>
        <span className="num">{from}</span>–<span className="num">{to}</span> of{' '}
        <span className="num">{meta.total}</span>
      </p>
      <div className="flex items-center gap-1">
        <button
          type="button"
          className="rounded-md p-1.5 hover:bg-slate-100 disabled:opacity-40"
          disabled={meta.page <= 1}
          onClick={() => onPageChange(meta.page - 1)}
          aria-label="Previous page"
        >
          <ChevronLeft className="size-4" />
        </button>
        <span className="num px-2">
          {meta.page} / {pages}
        </span>
        <button
          type="button"
          className="rounded-md p-1.5 hover:bg-slate-100 disabled:opacity-40"
          disabled={meta.page >= pages}
          onClick={() => onPageChange(meta.page + 1)}
          aria-label="Next page"
        >
          <ChevronRight className="size-4" />
        </button>
      </div>
    </div>
  );
}
